/**
 * Chat Store — Central Zustand state for all chat-related data.
 * This holds every UI state that was previously useState in ChatContext.
 */
import { create } from 'zustand';

const ACTIVE_CHAT_STORAGE_KEY = 'legal-advisor-active-chat-id';

const useChatStore = create((set) => ({
  // ── Conversations ──────────────────────────────────────────
  chats: [],
  chatsInitialized: false,
  chatsSource: 'none', // 'none' | 'guest' | 'cloud'
  currentChatId: null,
  isLoadingChats: false,

  setChats: (next) => set((state) => ({
    chats: typeof next === 'function' ? next(state.chats) : next,
  })),
  setChatsInitialized: (v) => set({ chatsInitialized: v }),
  setChatsSource: (v) => set({ chatsSource: v }),
  setCurrentChatId: (id) => set({ currentChatId: id }),
  setIsLoadingChats: (v) => set({ isLoadingChats: v }),

  // ── Typing / Streaming ─────────────────────────────────────
  isTyping: false,
  typingLabel: null,
  typingMode: 'question',
  analysisStepIndex: 0,

  setIsTyping: (v) => set({ isTyping: v }),
  setTypingLabel: (v) => set({ typingLabel: v }),
  setTypingMode: (v) => set({ typingMode: v }),
  setAnalysisStepIndex: (v) => set({ analysisStepIndex: v }),

  // ── Documents ──────────────────────────────────────────────
  documentContexts: {},
  pendingDocument: null,

  setDocumentContexts: (next) => set((state) => ({
    documentContexts: typeof next === 'function' ? next(state.documentContexts) : next,
  })),
  setPendingDocument: (v) => set({ pendingDocument: v }),

  // ── Drafting ───────────────────────────────────────────────
  draftModeByChatId: {},

  setDraftModeByChatId: (next) => set((state) => ({
    draftModeByChatId: typeof next === 'function' ? next(state.draftModeByChatId) : next,
  })),

  // ── Artifact Panel ─────────────────────────────────────────
  // Original shape: { isOpen, type, title, content, messageId, conversationId, sourcePrompt, sourceLanguage }
  artifactPanel: {
    isOpen: false,
    type: null,
    title: '',
    content: '',
    messageId: null,
    conversationId: null,
    sourcePrompt: '',
    sourceLanguage: 'ar',
  },
  artifactByMessageId: {},

  setArtifactPanel: (next) => set((state) => ({
    artifactPanel: typeof next === 'function' ? next(state.artifactPanel) : next,
  })),
  setArtifactByMessageId: (next) => set((state) => ({
    artifactByMessageId: typeof next === 'function' ? next(state.artifactByMessageId) : next,
  })),

  // ── Composer ───────────────────────────────────────────────
  composerPrefill: '',
  quotedMessage: null,

  setComposerPrefill: (v) => set({ composerPrefill: v }),
  setQuotedMessage: (v) => set({ quotedMessage: v }),

  // ── Report Panel ───────────────────────────────────────────
  reportPanel: {
    isOpen: false,
    report: null,
    messageId: null,
  },

  setReportPanel: (v) => set({ reportPanel: v }),

  // ── Message Count ──────────────────────────────────────────
  totalMessageCount: (() => {
    try {
      return parseInt(localStorage.getItem('legal-advisor-message-count') || '0', 10);
    } catch { return 0; }
  })(),

  setTotalMessageCount: (v) => set({ totalMessageCount: v }),
  incrementMessageCount: () => set((state) => {
    const next = state.totalMessageCount + 1;
    try { localStorage.setItem('legal-advisor-message-count', String(next)); } catch {}
    return { totalMessageCount: next };
  }),
}));

export default useChatStore;
export { ACTIVE_CHAT_STORAGE_KEY };
